// AutomationFilters, not the model namespaces: `parseProcessorsSearch` is the route's
// `validateSearch`, which stays in the entry chunk (docs/code-splitting.md).
import {
  type Automation,
  AutomationFilters,
  type AutomationProcessor,
  type AutomationWire,
} from '~api';

/**
 * @desc The Processors page's URL-held state (round 3, task 4.6): the list filters and, as
 * `processor`, the id whose drawer is open — so a workflow step can link to its processor.
 */
export type ProcessorsSearch = {
  type?: AutomationProcessor.Type;
  status?: Automation.EntityStatus;
  processor?: string;
};

const OBJECT_ID = /^[0-9a-f]{24}$/i;
const statuses = Object.values(AutomationFilters.entityStatuses) as readonly string[];
const types = AutomationFilters.processorTypes as readonly string[];

/** @desc Lenient: an unknown or malformed value is dropped rather than refusing the page. */
export function parseProcessorsSearch(search: Record<string, unknown>): ProcessorsSearch {
  const type =
    typeof search.type === 'string' && types.includes(search.type)
      ? (search.type as AutomationProcessor.Type)
      : undefined;
  const status =
    typeof search.status === 'string' && statuses.includes(search.status)
      ? (search.status as Automation.EntityStatus)
      : undefined;
  const processor =
    typeof search.processor === 'string' && OBJECT_ID.test(search.processor)
      ? search.processor
      : undefined;
  return {
    ...(type ? { type } : {}),
    ...(status ? { status } : {}),
    ...(processor ? { processor } : {}),
  };
}

export type ProcessorsSearchPatch = { [K in keyof ProcessorsSearch]?: ProcessorsSearch[K] | null };

/** @desc Merge a change into the search; `null` clears that key. */
export const withProcessorsSearch = (
  current: ProcessorsSearch,
  patch: ProcessorsSearchPatch,
): ProcessorsSearch => parseProcessorsSearch({ ...current, ...patch });

/**
 * @desc What an empty Processors list says (round 3 sweep): with no filter set, that none exist
 * yet and where they come from; with one, that the filters left nothing.
 */
export const processorsEmptyCopy = (search: ProcessorsSearch): string =>
  search.type || search.status
    ? 'No processors match these filters.'
    : 'No processors yet. Create one here, or Ario saves one when a workflow needs it.';

/** @desc The `automations.processor.list` input; asking for archived ones by status includes them. */
export const processorsListInput = (
  search: ProcessorsSearch,
): AutomationWire.ListProcessorsInput => ({
  limit: AutomationFilters.MAX_LIST,
  ...(search.type ? { type: search.type } : {}),
  ...(search.status ? { status: search.status } : {}),
  ...(search.status === AutomationFilters.entityStatuses.Archived ? { includeArchived: true } : {}),
});

/** @desc "Used by 2 workflows", or that it is not used yet. */
export const usedByLabel = (uses: readonly AutomationWire.ProcessorUse[]): string => {
  const workflows = new Set(uses.map((use) => use.workflowId)).size;
  if (workflows === 0) return 'Not used yet';
  return `${workflows} workflow${workflows === 1 ? '' : 's'}`;
};
